import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { TillCloseDialog } from "@/features/staff/till-close-dialog"
import { TillFigure } from "@/features/staff/till-figure"
import { tTill, type ShiftReport } from "@/features/staff/till"

/**
 * What the drawer is worth right now, and the one control that acts on it.
 *
 * The three inputs sit on one line and the expected total sits alone below
 * them, because they are not four peer figures: the first three are how the
 * fourth was reached. That is also the order a cashier reconstructs the number
 * in their head when it looks wrong — float, then cash sales, then the
 * movements they made themselves.
 *
 * The close button lives here rather than in the page header. Closing acts on
 * the expected total, so it sits under the number it settles, and the cashier
 * reads that number on the way to the button every time.
 */

type TillSummaryProps = {
  report: ShiftReport
  timeZone: string
}

export function TillSummary({ report, timeZone }: TillSummaryProps) {
  return (
    <Card density="compact">
      <CardHeader>
        <CardTitle>{tTill("open_title")}</CardTitle>
        <CardDescription>
          {tTill("opened_by", { name: report.shift.openedBy ?? tTill("unknown_actor") })}
          {" · "}
          {formatDateTime(report.shift.openedAt, { timeZone })}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <TillFigure
            label={tTill("opening_float_label")}
            value={formatMoney(report.openingFloat)}
          />
          <TillFigure label={tTill("cash_sales")} value={formatMoney(report.cashSales)} />
          <TillFigure
            label={tTill("movements_total")}
            value={formatMoney(report.movementsTotal)}
          />
        </div>

        <div className="border-t pt-4">
          <TillFigure
            label={tTill("expected_cash")}
            value={formatMoney(report.expectedCash)}
            hint={tTill("expected_cash_hint")}
            testId="shift-expected-cash"
            emphasis
          />
        </div>
      </CardContent>

      <CardFooter>
        <TillCloseDialog report={report} />
      </CardFooter>
    </Card>
  )
}
